import { FRAXLEND_PAIR_ABI } from '@/config/abis/FraxlendPair.abi'
import { paths } from '@/config/paths'
import { useAccount } from '@/domain/hooks/useAccount'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { type PairDetails, useIsolatedPairs } from '@/features/isolated/logic/useIsolatedPairs'
import { Address, formatUnits, zeroAddress } from 'viem'
import { useReadContracts } from 'wagmi'
const SECONDS_PER_YEAR = 31_556_952

export interface UseMyPortfolioResults {
  deposits: (Deposit & { rowClickOptions: { destination: string } })[]
  borrows: (Borrow & { rowClickOptions: { destination: string } })[]
  isLoading: boolean
  guestMode: boolean
}

export interface Deposit {
  collateralToken: Token
  borrowToken: Token
  lendAmount: NormalizedUnitNumber
  poolAvailable?: NormalizedUnitNumber
  supplyAPY: Percentage | undefined
  type: 'lending' | 'collateral'
  chainId: number
  pairAddress: Address
}

export interface Borrow {
  collateralToken: Token
  borrowToken: Token
  collateralAmount: NormalizedUnitNumber
  borrowAmount: NormalizedUnitNumber
  borrowAPY: Percentage | undefined
  chainId: number
  pairAddress: Address
}

export function useMyPortfolio(): UseMyPortfolioResults {
  const { pairs, isLoading } = useIsolatedPairs()
  return useMyPortfolioForPairs({ pairs, isPairsLoading: isLoading })
}

export function useMyPortfolioForPairs({
  pairs,
  isPairsLoading = false,
}: {
  pairs: PairDetails[]
  isPairsLoading?: boolean
}): UseMyPortfolioResults {
  const account = useAccount()
  const { chainId } = usePageChainId()

  // Get user snapshots for all pairs
  const userSnapshotReads =
    pairs?.map((pair) => ({
      address: pair.address,
      abi: FRAXLEND_PAIR_ABI,
      functionName: 'getUserSnapshot',
      args: [account || zeroAddress],
      chainId,
    })) || []

  const { data: userSnapshots, isLoading: isLoadingSnapshots } = useReadContracts({
    contracts: userSnapshotReads,
  }) as { data: any[]; isLoading: boolean }

  // Get convertToAssets reads for all pairs with user asset shares
  const convertToAssetsReads =
    pairs
      ?.map((pair, index) => {
        const snapshot = userSnapshots?.[index]?.result as readonly [bigint, bigint, bigint] | undefined
        if (!snapshot) return null
        const [userAssetShares] = snapshot
        if (userAssetShares === 0n) return null
        return {
          address: pair.address,
          abi: FRAXLEND_PAIR_ABI,
          functionName: 'convertToAssets',
          args: [userAssetShares],
          chainId,
        }
      })
      .filter((read): read is NonNullable<typeof read> => read !== null) || []

  // Get toBorrowAmount reads for all pairs with user borrow shares
  const toBorrowAmountReads =
    pairs
      ?.map((pair, index) => {
        const snapshot = userSnapshots?.[index]?.result as readonly [bigint, bigint, bigint] | undefined
        if (!snapshot) return null
        const [, userBorrowShares] = snapshot
        if (userBorrowShares === 0n) return null
        return {
          address: pair.address,
          abi: FRAXLEND_PAIR_ABI,
          functionName: 'toBorrowAmount',
          args: [userBorrowShares, false, false], // roundUp=false, previewInterest=false
          chainId,
        }
      })
      .filter((read): read is NonNullable<typeof read> => read !== null) || []

  const { data: convertToAssetsResults } = useReadContracts({
    contracts: convertToAssetsReads,
  }) as { data: any[]; isLoading: boolean }

  const { data: toBorrowAmountResults } = useReadContracts({
    contracts: toBorrowAmountReads,
  }) as { data: any[]; isLoading: boolean }

  const rateInfoReads =
    pairs?.map((pair) => ({
      address: pair.address,
      abi: FRAXLEND_PAIR_ABI,
      functionName: 'previewAddInterest',
      chainId,
    })) || []

  const { data: rateInfos } = useReadContracts({
    contracts: rateInfoReads,
  }) as { data: any[]; isLoading: boolean }

  const deposits: Deposit[] = []
  const borrows: Borrow[] = []

  if (pairs && userSnapshots && rateInfos) {
    let convertToAssetsIndex = 0
    let toBorrowAmountIndex = 0

    pairs.forEach((pair, index) => {
      const snapshot = userSnapshots[index]?.result as readonly [bigint, bigint, bigint] | undefined
      const rateInfo = rateInfos[index]?.result as
        | [
            bigint,
            bigint,
            bigint,
            { lastBlock: number; feeToProtocolRate: number; lastTimestamp: bigint; ratePerSec: bigint },
            { amount: bigint; shares: bigint },
            { amount: bigint; shares: bigint },
          ]
        | undefined
      if (!snapshot || !rateInfo) return

      const [userAssetShares, userBorrowShares, userCollateralBalance] = snapshot
      const [, , , { feeToProtocolRate: _feeToProtocolRate, ratePerSec }, , ,] = rateInfo

      const ratePerSecNum = Number(ratePerSec) / 1e18
      // feeToProtocolRate is 1e5 precision
      // see: https://github.com/FraxFinance/fraxlend-audit/blob/76c537d429ea4ac9bed2b13fc2cca90cccb2791a/src/contracts/FraxlendPairCore.sol#L103
      const feeToProtocolRate = Number(_feeToProtocolRate) / 1e5

      const borrowAPY = ratePerSecNum > 0 ? (Math.exp(ratePerSecNum * SECONDS_PER_YEAR) - 1) * 100 : 0

      const utilization = pair.utilization

      if (userAssetShares > 0n) {
        const lendAmount = convertToAssetsResults?.[convertToAssetsIndex]?.result as bigint | undefined
        convertToAssetsIndex++
        deposits.push({
          collateralToken: pair.borrowToken,
          borrowToken: pair.collateralToken,
          lendAmount: NormalizedUnitNumber(lendAmount ? formatUnits(lendAmount, pair.borrowToken.decimals) : '0'),
          poolAvailable: NormalizedUnitNumber(formatUnits(pair.available, pair.borrowToken.decimals)),
          supplyAPY: Percentage((borrowAPY * (utilization / 100) * (1 - feeToProtocolRate)) / 100, true, true),
          type: 'lending',
          chainId,
          pairAddress: pair.address,
        })
      }

      if (userBorrowShares > 0n) {
        const borrowAmount = toBorrowAmountResults?.[toBorrowAmountIndex]?.result as bigint | undefined
        toBorrowAmountIndex++

        borrows.push({
          collateralToken: pair.collateralToken,
          borrowToken: pair.borrowToken,
          collateralAmount: NormalizedUnitNumber(formatUnits(userCollateralBalance, pair.collateralToken.decimals)),
          borrowAmount: NormalizedUnitNumber(borrowAmount ? formatUnits(borrowAmount, pair.borrowToken.decimals) : '0'),
          borrowAPY: Percentage(borrowAPY / 100, true, true),
          chainId,
          pairAddress: pair.address,
        })
      }

      if (userCollateralBalance > 0n) {
        deposits.push({
          collateralToken: pair.collateralToken,
          borrowToken: pair.borrowToken,
          lendAmount: NormalizedUnitNumber(formatUnits(userCollateralBalance, pair.collateralToken.decimals)),
          supplyAPY: Percentage(0, true, true),
          type: 'collateral',
          chainId,
          pairAddress: pair.address,
        })
      }
    })
  }

  return {
    deposits: deposits.map((deposit) => ({
      ...deposit,
      rowClickOptions: getRowClickOptions(chainId, deposit.pairAddress),
    })),
    borrows: borrows.map((borrow) => ({
      ...borrow,
      rowClickOptions: getRowClickOptions(chainId, borrow.pairAddress),
    })),
    isLoading: isPairsLoading || isLoadingSnapshots,
    guestMode: !account,
  }
}

function getRowClickOptions(chainId: number, pairAddress: Address): { destination: string } {
  return {
    destination: paths.isolatedDetails.replace(':chainId', chainId.toString()).replace(':pairAddress', pairAddress),
  }
}
